import { navigate } from '@reach/router';
import { Formik, FormikHelpers } from 'formik';
import React, { useRef, useState } from 'react';
import { useApiRequest } from '../../../api/useApiRequest';
import { AssertUserHasPermission } from '../../../authorisation/AssertUserHasPermission';
import { RouteComponentProps } from '../../../helpers/RouteComponentProps';
import { urls } from '../../../helpers/urls';
import { BreadcrumbBar, BreadcrumbLink, BreadcrumbSeparator } from '../../../layout/Breadcrumbs';
import { PageContainer } from '../../../layout/Layout';
import { FormErrorBox } from '../../../shared/form/Form';
import { Header1 } from '../../../shared/Headers';
import { Project, ProjectHeader } from '../../projects/project-section/ProjectHeader';
import { projectUrls } from '../../projects/projectUrls';
import { GetDataForQuoteOrDesignForm } from '../../shared/quote-or-design-form/GetDataForQuoteOrDesignForm';
import { CreateQuoteResponse } from '../createQuoteResponse';
import { GenerateBillOfMaterialsForQuoteModal } from '../GenerateBillOfMaterialsForQuoteModal';
import { CreateUpdateQuoteCommand, mapQuoteFormModelToCommand } from '../createUpdateQuoteDto';
import { navigateAfterSavingQuote } from '../quote-form/navigateAfterSavingQuote';
import { NextStepAfterSavingQuote, QuoteForm } from '../quote-form/QuoteForm';
import { getInitialQuoteFormModel, QuoteFormModel, quoteFormModelValidator } from '../quote-form/quoteFormModel';

type Props = { project: Project } & RouteComponentProps;

export const CreateQuote = (props: Props) => {
  const createQuoteRequest = useApiRequest<CreateQuoteResponse, CreateUpdateQuoteCommand>({
    httpMethod: 'post',
    endpointUrl: `projects/${props.project.projectId}/quotes`,
  });

  const [savedQuoteId, setSavedQuoteId] = useState<number | null>(null);
  const nextStepAfterSave = useRef<NextStepAfterSavingQuote>('BackToProject');

  const onSubmit = (formModel: QuoteFormModel, formikHelpers: FormikHelpers<QuoteFormModel>) => {
    const createQuoteCommand: CreateUpdateQuoteCommand = mapQuoteFormModelToCommand(formModel, props.project.projectId);

    if (createQuoteRequest.inProgress) {
      return;
    }

    return createQuoteRequest.sendRequest({
      requestBody: createQuoteCommand,
      onSuccess: response => {
        formikHelpers.setSubmitting(false);

        void navigateAfterSavingQuote(
          props.project.projectId,
          response.quoteId,
          createQuoteCommand.quote.isDraft,
          nextStepAfterSave.current,
          setSavedQuoteId,
        );
      },
      onFailure: () => {
        formikHelpers.setSubmitting(false);
      },
    });
  };

  return (
    <>
      {savedQuoteId != null && (
        <GenerateBillOfMaterialsForQuoteModal
          projectId={props.project.projectId}
          quoteId={savedQuoteId}
          isOpen={true}
          closeModal={() => navigate(projectUrls.editQuote(props.project.projectId, savedQuoteId))}
          shouldRegenerate={false}
        />
      )}
      <BreadcrumbBar>
        <BreadcrumbLink to={urls.home}>Projects</BreadcrumbLink>
        <BreadcrumbSeparator />
        <BreadcrumbLink to={projectUrls.projectDetails(props.project.projectId)}>
          {props.project.projectReference}
        </BreadcrumbLink>
        <BreadcrumbSeparator />
        <div>New Quote</div>
      </BreadcrumbBar>
      <ProjectHeader project={props.project} />
      <PageContainer>
        <AssertUserHasPermission requiredPermission="Edit">
          <GetDataForQuoteOrDesignForm project={props.project}>
            {partOptions => (
              <>
                <Header1>New Quote</Header1>
                <Formik<QuoteFormModel>
                  onSubmit={onSubmit}
                  initialValues={getInitialQuoteFormModel(props.project)}
                  validate={formModel => quoteFormModelValidator.validate(formModel)}
                >
                  {formProps => (
                    <QuoteForm
                      {...formProps}
                      project={props.project}
                      dataForQuoteOrDesignForm={partOptions}
                      setNextStepAfterSave={nextStep => {
                        nextStepAfterSave.current = nextStep;
                      }}
                    />
                  )}
                </Formik>
                {createQuoteRequest.error && <FormErrorBox error={createQuoteRequest.error} />}
              </>
            )}
          </GetDataForQuoteOrDesignForm>
        </AssertUserHasPermission>
      </PageContainer>
    </>
  );
};
